import moment from 'moment-timezone';
import {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import StoreList from './store-list';
import Modal from './modal';
import RescheduleForm from './reschedule-form';
import {useRepair} from '../context/repair-context';
import {usePageText} from '../context/page-text-context';
import {schedulingLoading, schedulingLoaded} from '../features/scheduling/schedulerSlice';

const RescheduleModal = ({modalOpen, setModalOpen}) => {
  const dispatch = useDispatch();
  const baseUrl = import.meta.env.VITE_REACT_APP_API_HOST;
  const {start, end, appointmentId, loading: schedulerLoading} = useSelector((state) => state.scheduler);
  const [schedulingError, setSchedulingError] = useState(null);
  const {repair, refetch} = useRepair();
  const pageText = usePageText();
  const {start: repairStart, end: repairEnd, timeZone, isPending, selectedStore, serviceOption} = repair;
  const {
    time: {rescheduleModal},
    tracking: {fixtSupportEmail},
    errorMessages,
  } = pageText;
  const [selectingStore, setSelectingStore] = useState(false);
  const startTime = moment.tz(repairStart, timeZone);
  const endTime = moment.tz(repairEnd, timeZone);

  useEffect(() => {
    if (serviceOption === 'IN_STORE' && !selectedStore.storeId) {
      setSelectingStore(true);
    }
  }, [selectedStore, serviceOption]);

  const handleReschedule = async () => {
    const requestUrl = `${baseUrl}/web_repairs/${repair.id}/reschedule`;
    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };
    const options = {
      headers,
      method: 'PATCH',
      body: JSON.stringify({scheduled_window_start: start, scheduled_window_end: end, appointment_id: appointmentId}),
    };
    dispatch(schedulingLoading());
    try {
      const response = await fetch(requestUrl, options);

      if (!response.ok) {
        const {errors} = await response.json();
        const errorMessages = errors?.map((error) => error?.message);
        throw new Error(errorMessages?.join(', ') || response.statusText);
      }

      setSchedulingError(null);
      dispatch(schedulingLoaded());
      setModalOpen(false);
      refetch();
    } catch (error) {
      dispatch(schedulingLoaded());
      setSchedulingError(error.message);
      console.error(error);
    }
  };

  const renderError = () => {
    if (schedulingError) {
      return (
        <div>
          <div className="text-red">
            {(
              <details>
                <summary>{errorMessages.errDetected}</summary>
                <p>{schedulingError}</p>
              </details>
            ) || (isPending ? errorMessages.errSchedulingRepair : errorMessages.errReschedulingRepair)}
          </div>
          <p>
            {rescheduleModal.helpContact}
            <a href={`mailto:${fixtSupportEmail}`}>{fixtSupportEmail}</a>
          </p>
        </div>
      );
    }
  };

  return (
    <div data-testid="reschedule-modal">
      {modalOpen && (
        <Modal handleModalClose={() => setModalOpen(false)} visible={modalOpen}>
          <div className="w-screen max-w-[90vw] h-auto md:w-[80vw] flex flex-col justify-center items-center py-4">
            {selectingStore ? (
              <StoreList handleModalClose={() => setModalOpen(false)} setSelectingStore={setSelectingStore} />
            ) : (
              <RescheduleForm
                endTime={endTime}
                error={schedulingError}
                handleHideModal={() => setModalOpen(false)}
                handleReschedule={handleReschedule}
                isPending={isPending}
                setSelectingStore={setSelectingStore}
                startTime={startTime}
                schedulingIsLoading={schedulerLoading}
              />
            )}
          </div>
        </Modal>
      )}

      {renderError()}
    </div>
  );
};

export default RescheduleModal;
